/**
 * Layout and emphasis for the normalized SQL template shown in the drawer.
 *
 * The stored preview is a single collapsed line capped at 200 characters, so it
 * arrives as prose and is routinely cut mid-statement with parentheses left
 * open. Both functions here are total: they lay out whatever they are given and
 * never assume the statement is complete or balanced.
 */

/** Keywords that begin a clause, so a line begins with them. Longest first. */
const CLAUSE_KEYWORDS = [
  "LEFT OUTER JOIN",
  "RIGHT OUTER JOIN",
  "FULL OUTER JOIN",
  "INNER JOIN",
  "CROSS JOIN",
  "LEFT JOIN",
  "RIGHT JOIN",
  "UNION ALL",
  "GROUP BY",
  "ORDER BY",
  "PARTITION BY",
  "INSERT INTO",
  "WITH",
  "SELECT",
  "FROM",
  "WHERE",
  "HAVING",
  "QUALIFY",
  "WINDOW",
  "LIMIT",
  "OFFSET",
  "UNION",
  "EXCEPT",
  "INTERSECT",
  "VALUES",
  "JOIN",
  "ON",
] as const;

/** Keywords that continue the clause above them, so they sit one level in. */
const CONTINUATION_KEYWORDS = ["AND", "OR"] as const;

/** The keywords that open a subquery, which is the only paren worth indenting. */
const SUBQUERY_OPENERS = new Set(["SELECT", "WITH"]);

const INDENT = "  ";

const KEYWORDS_FOR_MATCH = [...CLAUSE_KEYWORDS, ...CONTINUATION_KEYWORDS];

interface Token {
  text: string;
  spaceBefore: boolean;
}

/**
 * Whether each token had whitespace in front of it is carried through, so
 * emitting a line reproduces the original spacing. That is what keeps
 * `DATE_SUB(CURRENT_DATE(), ...)` intact while the clauses around it move.
 */
function tokenize(sql: string): Token[] {
  const tokens: Token[] = [];
  const pattern = /(\s*)([(),]|[^\s(),]+)/g;

  for (const match of sql.matchAll(pattern)) {
    tokens.push({ text: match[2] ?? "", spaceBefore: (match[1] ?? "").length > 0 });
  }

  return tokens;
}

/** How many tokens the keyword starting here spans, or 0 for none. */
function keywordLength(tokens: Token[], index: number): number {
  for (const keyword of KEYWORDS_FOR_MATCH) {
    const words = keyword.split(" ");
    const matches = words.every(
      (word, offset) => tokens[index + offset]?.text.toUpperCase() === word,
    );
    if (matches) return words.length;
  }

  return 0;
}

function isClauseKeyword(keyword: string): boolean {
  return CLAUSE_KEYWORDS.some((entry) => entry === keyword.toUpperCase());
}

export function formatNormalizedSql(sql: string): string[] {
  const tokens = tokenize(sql.trim());
  if (tokens.length === 0) return [];

  const lines: string[] = [];
  let current = "";
  let depth = 0;
  // Only subquery parens indent. Function-call parens are pushed as `false` so
  // their closer does not pull a level back out.
  const parens: boolean[] = [];
  let callDepth = 0;

  const flush = () => {
    // Indentation with nothing on it is not a line. Opening a subquery lays the
    // next line's indent down before its first keyword arrives, and that keyword
    // starts a line of its own.
    if (current.trim().length > 0) lines.push(current);
    current = "";
  };
  const start = (extra = 0) => {
    flush();
    current = INDENT.repeat(depth + extra);
  };
  const append = (token: Token) => {
    const glue = token.spaceBefore && current.trim().length > 0 ? " " : "";
    current += `${glue}${token.text}`;
  };

  for (let index = 0; index < tokens.length; index += 1) {
    const token = tokens[index];
    if (!token) continue;

    const spanned = keywordLength(tokens, index);
    if (spanned > 0 && callDepth === 0) {
      const keyword = tokens
        .slice(index, index + spanned)
        .map((entry) => entry.text)
        .join(" ");

      // A continuation sits one level in from the clause it qualifies.
      start(isClauseKeyword(keyword) ? 0 : 1);
      current += keyword;
      index += spanned - 1;
      continue;
    }

    if (token.text === "(") {
      const opensSubquery = SUBQUERY_OPENERS.has(tokens[index + 1]?.text.toUpperCase() ?? "");
      parens.push(opensSubquery);
      append(token);
      if (opensSubquery) {
        depth += 1;
        flush();
        current = INDENT.repeat(depth);
      } else {
        callDepth += 1;
      }
      continue;
    }

    if (token.text === ")") {
      // A preview cut mid-statement can close a paren that was never opened.
      const opensSubquery = parens.pop() ?? false;
      if (opensSubquery) {
        depth = Math.max(0, depth - 1);
        start();
        current += ")";
      } else {
        callDepth = Math.max(0, callDepth - 1);
        append(token);
      }
      continue;
    }

    append(token);

    // A comma inside a function's arguments belongs to that call, not the list.
    if (token.text === "," && callDepth === 0) start(1);
  }

  flush();
  return lines;
}

/**
 * Keywords that colour but never break a line.
 *
 * Deliberately separate from the layout lists above: adding a word there moves
 * the statement's shape, and `DISTINCT` or `DESC` starting a line of its own
 * would be wrong. Colouring `FROM` while leaving `DISTINCT` beside it plain
 * reads as highlighting that has failed rather than as restraint, which is the
 * only reason this list exists.
 */
const INLINE_KEYWORDS = [
  "IS NOT NULL",
  "NOT BETWEEN",
  "IS NULL",
  "NOT IN",
  "NOT LIKE",
  "BETWEEN",
  "DISTINCT",
  "INTERVAL",
  "OVER",
  "USING",
  "EXISTS",
  "CASE",
  "WHEN",
  "THEN",
  "ELSE",
  "END",
  "ASC",
  "DESC",
  "LIKE",
  "NULL",
  "TRUE",
  "FALSE",
  "NOT",
  "AS",
  "IN",
  "IS",
] as const;

/** Longest first, so `IS NOT NULL` wins over `IS`. */
const HIGHLIGHT_KEYWORDS = [...KEYWORDS_FOR_MATCH, ...INLINE_KEYWORDS];

const HIGHLIGHT_KEYWORD_SET: ReadonlySet<string> = new Set<string>(HIGHLIGHT_KEYWORDS);

export type SqlSegmentKind = "keyword" | "identifier" | "call" | "placeholder" | "plain";

export interface SqlSegment {
  text: string;
  kind: SqlSegmentKind;
}

/**
 * Four things earn a colour, and each answers a question an operator brings to
 * a normalized template: what shape the statement has (`keyword`), what it read
 * (`identifier` — the backticked table path), what it computed (`call`), and
 * where the values it ran with used to be (`placeholder`).
 *
 * Everything else — column names, operators, aliases — stays plain. They are the
 * bulk of the text, and colouring them would leave nothing standing out.
 *
 * The alternatives are ordered by precedence, not by likelihood: a keyword can
 * sit in front of a paren (`IN (`) and must not be read as a call.
 */
const SEGMENT_PATTERN = new RegExp(
  [
    // A preview cut mid-identifier leaves the closing backtick off.
    "`[^`]*`?",
    "\\?|@[A-Za-z_]\\w*",
    `\\b(?:${HIGHLIGHT_KEYWORDS.map((keyword) => keyword.replaceAll(" ", "\\s+")).join("|")})\\b`,
    "\\b[A-Za-z_]\\w*(?=\\s*\\()",
  ].join("|"),
  "gi",
);

/** Collapses the whitespace a multi-word keyword may have been matched across. */
function isKeyword(text: string): boolean {
  return HIGHLIGHT_KEYWORD_SET.has(text.toUpperCase().replace(/\s+/g, " "));
}

function segmentKind(text: string): SqlSegmentKind {
  if (text.startsWith("`")) return "identifier";
  if (text.startsWith("?") || text.startsWith("@")) return "placeholder";
  return isKeyword(text) ? "keyword" : "call";
}

export function highlightSqlSegments(line: string): SqlSegment[] {
  const segments: SqlSegment[] = [];
  let cursor = 0;

  for (const match of line.matchAll(SEGMENT_PATTERN)) {
    const at = match.index;
    const text = match[0];
    if (at > cursor) segments.push({ text: line.slice(cursor, at), kind: "plain" });
    segments.push({ text, kind: segmentKind(text) });
    cursor = at + text.length;
  }

  if (cursor < line.length) segments.push({ text: line.slice(cursor), kind: "plain" });
  return segments;
}
